import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Stack,
  useTheme,
  CircularProgress,
  Alert,
} from "@mui/material";
import apiService from "../../service/Api";
import type { OpsDashboardData } from "./types/opsDashboard";
import ActiveServiceFocus from "./components/ActiveServiceFocus";
import CrewAvailabilityWidget from "./components/CrewAvailabilityWidget";
import QuickActions from "./components/QuickActions";
import RecordingsHandoffTable from "./components/RecordingsHandoffTable";
import SmartAlertsWidget from "./components/SmartAlertsWidget";
import StatCardsDashboard from "./components/StatCardsDashboard";
import WeeklyScheduleWidget from "./components/WeeklyScheduleWidget";
import NavBar from "../mms-landingpage/components/NavBar";
import { tokens } from "../../theme";

export default function OperationsDashboard() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [data, setData] = useState<OpsDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService
      .get<OpsDashboardData>("/api/management/dashboard/ops-dashboard/")
      .then((res) => setData(res.data))
      .catch(() => setError("Failed to load operations dashboard data"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colors.primary[800],
          display: "flex",
          flexDirection: "column",
        }}
      >
        <NavBar />
        <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: colors.primary[800],
        display: "flex",
        flexDirection: "column",
      }}
    >
      <NavBar />
      <Box
        sx={{
          p: 3,
          flexGrow: 1,
        }}
      >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} alignItems="stretch" >
        <Grid size={{ xs: 12, md: 4, lg: 3 }}>
          <Stack spacing={2} sx={{ height: "100%" }}>
            <WeeklyScheduleWidget schedule={data?.weekly_schedule} />
            <SmartAlertsWidget
              alerts={data?.smart_alerts}
              sx={{ flexGrow: 1, height: "100%" }}
            />
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 8, lg: 6 }}>
          <Stack spacing={2} sx={{ height: "100%" }}>
            <StatCardsDashboard stats={data?.stats} />
            <ActiveServiceFocus stream={data?.upcoming_streams?.[0] ?? null} />
            <RecordingsHandoffTable sx={{ flexGrow: 1, height: "100%" }} />
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 3 }}>
          <Stack spacing={2} sx={{ height: "100%" }}>
            <QuickActions />
            <CrewAvailabilityWidget
              crewAvailability={data?.crew_availability}
              sx={{ flexGrow: 1, height: "100%" }}
            />
          </Stack>
        </Grid>

        {/* <Grid size={{ xs: 12 }}>
          <OperationsMap />
        </Grid> */}
      </Grid>
      </Box>
    </Box>
  );
}
